import { useMemo } from "react";

import {
    ExternalYieldInfo,
    LoopVaultInfo,
    TokenListMetadata,
    LoanValueTuple,
    LoopVaultInfoExpanded,
    Protocol,
    LoopTag,
    LoopFilterTags,
    LoopAssetTypeTag,
    LoopMetadataTag,
    OracleQuote,
    LoanPnlStats,
    PnLDataPoint,
    PositionValueDataPoint,
    RateHistoryDataPoint,
    ChartDataMarker
} from "@bridgesplit/abf-sdk";
import {
    TIME,
    bpsToUiDecimals,
    filterTruthy,
    getUnixTs,
    lamportsToUiAmount,
    stringLamportsToUiAmountDecimal,
    percentUiToDecimals,
    NullableRecord,
    combineCollections,
    filterNullableRecord,
    roundToDecimals,
    bigIntAmountTag,
    bigIntPercentTag
} from "@bridgesplit/utils";
import Decimal from "decimal.js";
import { BN } from "bn.js";

import {
    BestQuote,
    PrincipalMarketStrategyStats,
    StrategyInfo,
    PrincipalMarketStats,
    BorrowCap,
    OrderbookQuote,
    MaxQuote,
    ParsedLendingVault,
    PartnerInfo,
    FarmingInfo,
    PartnerPoint,
    PartnerReward,
    ParsedTimeLock,
    LendingVaultAllocation,
    LoanCollateral,
    AssetTypeIdentifier,
    LoanEventActionType,
    LoanMatrixEvent,
    ParsedLoanEventExpanded,
    ParsedLoanEvent,
    LendingVaultMetadata,
    ParsedVaultRewardsSchedule,
    UserVaultExternalRewardsPnlResponse,
    PortfolioRewards,
    PortfolioRewardsBreakdown,
    StrategyRewards,
    BreakdownActivity,
    LedgerReward,
    VaultReward,
    EarnVaultReward,
    RewardPointsPerSecond,
    LoopscalePointsAction,
    ParsedMarketInformationWithLtvInfo,
    ParsedLoanEventActionMetadata,
    DerivedLoanEventActionMetadata,
    DerivedLoanEventActionType,
    LiquidateEventType,
    ParsedEventLedger,
    getPositionYield,
    StrategyTerms,
    AssetTerms,
    Ledger,
    LoanCollateralExpanded,
    UserPositionDepositSummaryResponse,
    LendingVaultExpanded,
    TokenRestrictionResponse,
    RestrictionExpanded,
    MarketInformationAssetData,
    ParsedMarketInformation,
    Strategy,
    ParsedPrincipalCaps,
    ParsedCapMonitor,
    StrategyExpandedInfo,
    ParsedLendingVaultInfo,
    TimeLockUpdateType,
    ParsedAddCollateralTimelockArgs,
    ParsedCollateralTerms,
    ParsedUpdateAssetDataParams,
    UpdateCapsParams,
    ParsedUpdateStrategySettingsParams,
    ParsedUpdateVaultSettingsParams,
    AbfLoanExpanded,
    LoanInfo,
    EarnExponentAccount,
    EarnWithdrawalRequest,
    EarnQueueResponse,
    EarnActivityResponse,
    EarnPortfolioEvent,
    MergeOptionGroup,
    MergePlannerRankedResponse,
    EarnPortfolioHistoryPoint,
    EarnPositionChartResponse,
    ParsedLiquidatedCollateralMetadata,
    RankedMergeOption
} from "../types";
import { isDomainEventActionMetadataType, isLiquidateEvent, isRepayEvent } from "./loan";
import {
    isWhirlpoolPosition,
    useTokenListContext,
    getPastOrPresentZcLoanCollateralMint,
    calculateLoanHealth,
    LoanHealthStatus,
    isLoanActive
} from "../api";
import { PositionValueChartData, PnlChartData, RateHistoryChartData, LoanChartsEvent } from "../types/api";

type GetDecimals = (mint: string) => number;
type GetMetadata = (mint: string) => TokenListMetadata | undefined;

// use a hook to allow a later replacement with an api call to a token list
export function useAbfTypesToUiConverter() {
    const { getMetadata, isLoading, getDecimals } = useTokenListContext();

    return useMemo(
        () => ({
            // Utils
            getMetadata,
            getDecimals,
            tokensLoading: isLoading,

            // Partners
            convertPartnerInfo: (rawPartner: PartnerInfo) => convertPartnerInfo(rawPartner),

            // Ecosystem/Farming
            convertFarmingInfo: (rawFarmingInfo: FarmingInfo) => convertFarmingInfo(rawFarmingInfo),
            convertPartnerPoint: (rawPoint: PartnerPoint) => convertPartnerPoint(rawPoint),
            convertPartnerReward: (rawReward: PartnerReward) => convertPartnerReward(rawReward),

            // Loans
            convertParsedLoanEventsToDerivedEvents: (events: ParsedLoanEvent[], matrixUpdates: LoanMatrixEvent[]) =>
                convertParsedLoanEventsToDerivedEvents(events, getMetadata, matrixUpdates),
            convertLoanInfo: (loan: LoanInfo) => convertLoanInfo(loan, getDecimals, getMetadata),

            // Strategies
            convertStrategyInfo: (strategy: StrategyInfo) => convertStrategyInfo(strategy, getDecimals),
            convertStrategyStats: (mint: string, stats: PrincipalMarketStrategyStats) =>
                convertStrategyStats(mint, stats, getDecimals),
            convertStrategyTerms: (terms: StrategyTerms, principalDecimals: number) =>
                convertStrategyTerms(terms, getDecimals, principalDecimals),

            // Loops
            convertLoopVault: (loopVault: LoopVaultInfo) => convertLoopVault(loopVault, getDecimals, getMetadata),

            // Lending vaults
            convertLendingVaultInfo: (vaultInfo: ParsedLendingVaultInfo) =>
                convertLendingVaultInfo(vaultInfo, getDecimals),
            convertLendingVaultAllocation,
            convertLendingVaultDepositSummary: (
                lendingVaultDepositSummary: UserPositionDepositSummaryResponse,
                vaultToPrincipalMint: Map<string, LendingVaultExpanded>
            ) => convertLendingVaultDepositSummary(lendingVaultDepositSummary, vaultToPrincipalMint, getDecimals),

            convertEarnQueueResponse: (resp: EarnQueueResponse) => convertEarnQueueResponse(resp),
            convertEarnActivityResponse: (resp: EarnActivityResponse) => convertEarnActivityResponse(resp),
            convertEarnPortfolioEvent: (event: EarnPortfolioEvent) => convertEarnPortfolioEvent(event),
            convertEarnPortfolioHistoryPoint: (point: EarnPortfolioHistoryPoint) =>
                convertEarnPortfolioHistoryPoint(point),
            convertEarnPositionChart: (resp: EarnPositionChartResponse) => convertEarnPositionChart(resp),

            // Token restrictions
            convertTokenRestrictions: (restrictions: TokenRestrictionResponse[]) =>
                convertTokenRestrictions(restrictions, getMetadata),

            convertExternalRewardsResponse: (externalRewardsResponse: UserVaultExternalRewardsPnlResponse) =>
                convertExternalRewardsResponse(externalRewardsResponse, getMetadata),

            // Quotes & Markets
            convertOfferQuote: <T extends MaxQuote | BestQuote>(quote: T, principalMint: string) =>
                convertOfferQuote(quote, principalMint, getDecimals),
            convertOrderbookQuote: (quote: OrderbookQuote, principalMint: string) =>
                convertOrderbookQuote(quote, principalMint, getDecimals),
            convertMergeLoanPlan: (quote: MergePlannerRankedResponse) => convertMergeLoanPlan(quote),
            convertBorrowCap: (cap: BorrowCap) => convertBorrowCap(cap, getDecimals),
            convertPrincipalMarketStats: (mint: string, stats: PrincipalMarketStats) =>
                convertPrincipalMarketStats(mint, stats, getDecimals),
            // Market information
            // Market information
            convertMarketInformationWithLtvInfo: (marketInformationWithLtvInfo: ParsedMarketInformationWithLtvInfo) =>
                convertMarketInformationWithLtvInfo(marketInformationWithLtvInfo, getDecimals),

            // Points breakdown
            convertPortfolioRewards: (
                portfolioRewards: PortfolioRewards | undefined,
                rewardPointsPerSecond: RewardPointsPerSecond
            ) => convertPortfolioRewardsBreakdown(portfolioRewards, rewardPointsPerSecond, getMetadata),

            convertLoanPnlStats: (
                pnlStats: LoanPnlStats | undefined,
                principalDecimals: number | undefined,
                collateralDecimals: number | undefined
            ) => convertLoanPnlStats(pnlStats, principalDecimals, collateralDecimals),

            // Chart data converters
            convertPositionValueChartData: (dataPoints: PositionValueDataPoint[]) =>
                convertPositionValueChartData(dataPoints),
            convertPnlChartData: (dataPoints: PnLDataPoint[], collateralDecimals?: number) =>
                convertPnlChartData(dataPoints, collateralDecimals),
            convertRateHistoryChartData: (dataPoints: RateHistoryDataPoint[]) =>
                convertRateHistoryChartData(dataPoints),
            convertLoanChartsEvents: (dataPoints: ChartDataMarker[]) => convertLoanChartsEvents(dataPoints),
            //Convert Time Locks
            convertTimelock: (timeLock: ParsedTimeLock) => convertTimelock(timeLock, getDecimals),
            isLoading
        }),
        [getMetadata, isLoading, getDecimals]
    );
}
export function groupEventsByEventTxn(events: ParsedLoanEvent[]): Map<string, ParsedLoanEvent[]> {
    const eventsByTxn = new Map<string, ParsedLoanEvent[]>();

    events.forEach((event) => {
        const txnEvents = eventsByTxn.get(event.eventTxn) || [];
        txnEvents.push(event);
        eventsByTxn.set(event.eventTxn, txnEvents);
    });

    return eventsByTxn;
}

function formatLedgerState(ledgerState: ParsedEventLedger, decimals: number | undefined): ParsedEventLedger {
    return {
        ...ledgerState,
        principalDue: lamportsToUiAmount(ledgerState.principalDue, decimals),
        interestOutstanding: lamportsToUiAmount(ledgerState.interestOutstanding, decimals),
        principalRepaid: lamportsToUiAmount(ledgerState.principalRepaid, decimals),
        apy: bpsToUiDecimals(ledgerState.apy)
    };
}

function formatCollateralTransfers(
    collateralTransfers: ParsedLiquidatedCollateralMetadata[],
    getMetadata: GetMetadata
): ParsedLiquidatedCollateralMetadata[] {
    return collateralTransfers.map((transfer) => ({
        ...transfer,
        amount: lamportsToUiAmount(transfer.amount, getMetadata(transfer.assetMint)?.decimals)
    }));
}

function convertRawLoanEvent(event: ParsedLoanEvent, getMetadata: GetMetadata): ParsedLoanEvent {
    const decimals = getMetadata(event.assetIdentifier)?.decimals;
    return {
        ...event,
        amount: lamportsToUiAmount(event.amount, decimals),
        actionMetadata: convertRawLoanEventActionMetadata(event.actionMetadata, event.assetIdentifier, getMetadata)
    };
}

function convertSafeLedger(
    ledgerState: ParsedEventLedger | undefined,
    decimals: number | undefined
): ParsedEventLedger | undefined {
    if (!ledgerState) return undefined;
    return formatLedgerState(ledgerState, decimals);
}

function convertRawLoanEventActionMetadata(
    actionMetadata: ParsedLoanEventActionMetadata,
    assetIdentifier: string,
    getMetadata: GetMetadata
): ParsedLoanEventActionMetadata {
    const decimals = getMetadata(assetIdentifier)?.decimals;
    const updateType = actionMetadata.type;

    switch (updateType) {
        case LoanEventActionType.RepayPrincipal: {
            const metadata = actionMetadata.params;
            const preRepaymentPrincipalLedgerState = convertSafeLedger(
                metadata.preRepaymentPrincipalLedgerState,
                decimals
            );
            const postRepaymentPrincipalLedgerState = convertSafeLedger(
                metadata.postRepaymentPrincipalLedgerState,
                decimals
            );

            if (!preRepaymentPrincipalLedgerState || !postRepaymentPrincipalLedgerState) return actionMetadata;

            return {
                type: updateType,
                params: {
                    preRepaymentPrincipalLedgerState,
                    postRepaymentPrincipalLedgerState
                }
            };
        }
        case LoanEventActionType.RefinanceLedger: {
            const metadata = actionMetadata.params;
            const preRefinanceLedgerState = convertSafeLedger(metadata.preRefinanceLedgerState, decimals);
            const postRefinanceLedgerState = convertSafeLedger(metadata.postRefinanceLedgerState, decimals);
            if (!preRefinanceLedgerState || !postRefinanceLedgerState) return actionMetadata;
            return {
                ...actionMetadata,
                params: { preRefinanceLedgerState, postRefinanceLedgerState }
            };
        }

        case LoanEventActionType.Liquidate: {
            const metadata = actionMetadata.params;
            const collateralTransfers = formatCollateralTransfers(metadata.collateralTransfers, getMetadata);
            if (!collateralTransfers) return actionMetadata;
            return {
                ...actionMetadata,
                params: { ...metadata, collateralTransfers }
            };
        }

        case LoanEventActionType.BorrowPrincipal: {
            const metadata = actionMetadata.params;
            const preBorrowPrincipalLedgerState = convertSafeLedger(metadata.preBorrowPrincipalLedgerState, decimals);
            const postBorrowPrincipalLedgerState = convertSafeLedger(metadata.postBorrowPrincipalLedgerState, decimals);
            if (!preBorrowPrincipalLedgerState || !postBorrowPrincipalLedgerState) return actionMetadata;
            return {
                ...actionMetadata,
                params: { preBorrowPrincipalLedgerState, postBorrowPrincipalLedgerState }
            };
        }

        default:
            return actionMetadata;
    }
}

function mergeLiquidateAndRepayEvents(
    liquidateEvent: ParsedLoanEvent,
    repayEvent: ParsedLoanEvent,
    matrixUpdates: LoanMatrixEvent[],
    getMetadata: GetMetadata
): ParsedLoanEventExpanded {
    if (!isDomainEventActionMetadataType(repayEvent.actionMetadata, LoanEventActionType.RepayPrincipal)) {
        throw new Error("Expected repay event metadata");
    }
    if (!isDomainEventActionMetadataType(liquidateEvent.actionMetadata, LoanEventActionType.Liquidate)) {
        throw new Error("Expected liquidate event metadata");
    }

    const isFullLiquidation = () => {
        return (
            postRepaymentPrincipalLedgerState.principalDue === postRepaymentPrincipalLedgerState.principalRepaid &&
            postRepaymentPrincipalLedgerState.interestOutstanding === 0
        );
    };
    const repayMetadata = repayEvent.actionMetadata.params;
    const { postRepaymentPrincipalLedgerState } = repayMetadata;
    const liquidateMetadata = liquidateEvent.actionMetadata.params;
    const matrixUpdateForEvent = matrixUpdates.filter((update) => update.txnSignature === liquidateEvent.eventTxn);

    const collateralTransfersExpanded = liquidateMetadata.collateralTransfers.map((transfer) => {
        const { assetMint, amount, toLiquidator, price } = transfer;
        const liquidateEventType = toLiquidator
            ? LiquidateEventType.LiquidatedCollateral
            : LiquidateEventType.CollateralReturned;
        return { assetMint, amount, toLiquidator, liquidateEventType, price };
    });

    const actionMetadata: DerivedLoanEventActionMetadata = isFullLiquidation()
        ? {
              type: DerivedLoanEventActionType.FullLiquidation,
              params: {
                  ledgerIndex: liquidateMetadata.ledgerIndex,
                  isTimeBasedLiquidation: liquidateMetadata.isTimeBasedLiquidation,
                  liquidationFee: liquidateMetadata.liquidationFee,
                  collateralTransfers: collateralTransfersExpanded,
                  repaymentEventMetadata: {
                      preRepaymentPrincipalLedgerState: repayMetadata.preRepaymentPrincipalLedgerState,
                      postRepaymentPrincipalLedgerState: repayMetadata.postRepaymentPrincipalLedgerState
                  }
              }
          }
        : {
              type: DerivedLoanEventActionType.PartialLiquidation,
              params: {
                  ledgerIndex: liquidateMetadata.ledgerIndex,
                  isTimeBasedLiquidation: liquidateMetadata.isTimeBasedLiquidation,
                  liquidationFee: liquidateMetadata.liquidationFee,
                  collateralTransfers: collateralTransfersExpanded,
                  repaymentEventMetadata: {
                      preRepaymentPrincipalLedgerState: repayMetadata.preRepaymentPrincipalLedgerState,
                      postRepaymentPrincipalLedgerState: repayMetadata.postRepaymentPrincipalLedgerState
                  }
              }
          };

    const mergedLiquidateEvent = {
        ...liquidateEvent,
        action: isFullLiquidation()
            ? DerivedLoanEventActionType.FullLiquidation
            : DerivedLoanEventActionType.PartialLiquidation,
        eventAssetMetadata: getMetadata(liquidateEvent.assetIdentifier),
        matrixSnapshots: matrixUpdateForEvent,
        actionMetadata
    };
    return mergedLiquidateEvent;
}

function toDerivedLoanEventActionMetadata(
    actionMetadata: ParsedLoanEventActionMetadata
): DerivedLoanEventActionMetadata {
    switch (actionMetadata.type) {
        case LoanEventActionType.AddCollateral:
            return { type: DerivedLoanEventActionType.AddCollateral, params: null };
        case LoanEventActionType.RemoveCollateral:
            return { type: DerivedLoanEventActionType.RemoveCollateral, params: null };
        case LoanEventActionType.BorrowPrincipal:
            return { type: DerivedLoanEventActionType.BorrowPrincipal, params: actionMetadata.params };
        case LoanEventActionType.RepayPrincipal:
            return { type: DerivedLoanEventActionType.RepayPrincipal, params: actionMetadata.params };
        case LoanEventActionType.RefinanceLedger:
            return { type: DerivedLoanEventActionType.RefinanceLedger, params: actionMetadata.params };
        case LoanEventActionType.SellLedger:
            return { type: DerivedLoanEventActionType.SellLedger, params: actionMetadata.params };
        case LoanEventActionType.Liquidate:
            return { type: DerivedLoanEventActionType.Liquidate, params: actionMetadata.params };
    }
}

//Takes in FORMATTED events, not raw events
function convertParsedLoanEventsToDerivedEvents(
    events: ParsedLoanEvent[],
    getMetadata: GetMetadata,
    matrixUpdates: LoanMatrixEvent[]
): ParsedLoanEventExpanded[] {
    const eventsByTxn = groupEventsByEventTxn(events);

    return Array.from(eventsByTxn.values()).flatMap((txnEvents) => {
        const repayEvent = txnEvents.find((e) => isRepayEvent(e.action));
        const liquidateEvent = txnEvents.find((e) => isLiquidateEvent(e.action));

        if (liquidateEvent && repayEvent) {
            return [mergeLiquidateAndRepayEvents(liquidateEvent, repayEvent, matrixUpdates, getMetadata)];
        }

        return txnEvents.map((event) => {
            const actionMetadata = toDerivedLoanEventActionMetadata(event.actionMetadata);
            return {
                ...event,
                action: actionMetadata.type,
                actionMetadata,
                eventAssetMetadata: getMetadata(event.assetIdentifier),
                matrixSnapshots: matrixUpdates.filter((update) => update.txnSignature === event.eventTxn)
            };
        });
    });
}

function convertLoanInfo(loan: LoanInfo, getDecimals: GetDecimals, getMetadata: GetMetadata): LoanInfo {
    const ledgers = loan.ledgers.map((l) => convertLoanLedger(l, getDecimals(l.principalMint)));
    const pastLedgers = loan.pastLedgers.map((l) => convertLoanLedger(l, getDecimals(l.principalMint)));
    const collateral = loan.collateral.map((c) => convertLoanCollateral(c, getDecimals(c.assetMint)));
    const events = loan.events.map((e) => convertRawLoanEvent(e, getMetadata));
    // closedSummary's principal/collateral avgBalance values are raw lamports per the BE
    // contract — convert to UI amounts here so consumers can render with BsMetaUtil.formatAmount
    // without an isRawAmount escape hatch (matches how the active ledger/collateral are handled).
    const closedSummary = loan.closedSummary
        ? {
              ...loan.closedSummary,
              principal: loan.closedSummary.principal.map((p) => ({
                  ...p,
                  avgBalance: lamportsToUiAmount(p.avgBalance, getDecimals(p.principal))
              })),
              collateral: loan.closedSummary.collateral.map((c) => ({
                  ...c,
                  avgBalance: lamportsToUiAmount(c.avgBalance, getDecimals(c.collateral))
              }))
          }
        : undefined;

    return {
        ...loan,
        loanType: loan.loanType,
        ledgers: ledgers,
        pastLedgers: pastLedgers,
        collateral: collateral,
        events: events,
        // Conditional spread: avoid setting an explicit undefined closedSummary key on active
        // loans (filterNullableRecord downstream drops rows with any undefined field).
        ...(closedSummary !== undefined && { closedSummary })
    };
}

export function getLoopVaultPairIdentifier(loan: AbfLoanExpanded): string | undefined {
    const principalMint = loan.principalMetadata?.mint;
    if (!principalMint) return undefined;
    const collateralMint = getPastOrPresentZcLoanCollateralMint(loan);
    if (!collateralMint) return undefined;

    return `${principalMint}-${collateralMint}`;
}

function convertLoanLedger(ledger: Ledger, decimals: number): Ledger {
    return {
        ...ledger,
        principalDue: lamportsToUiAmount(ledger.principalDue, decimals),
        principalRepaid: lamportsToUiAmount(ledger.principalRepaid, decimals),
        interestOutstanding: lamportsToUiAmount(ledger.interestOutstanding, decimals),
        interestPerSecond: lamportsToUiAmount(ledger.interestPerSecond, decimals),
        apy: bpsToUiDecimals(ledger.apy),
        weights: ledger.weights.map((w: number) => bpsToUiDecimals(w)) as LoanValueTuple,
        ltvRatios: ledger.ltvRatios.map((l: number) => bpsToUiDecimals(l)) as LoanValueTuple,
        lqtRatios: ledger.lqtRatios.map((l: number) => bpsToUiDecimals(l)) as LoanValueTuple
    };
}

function convertLoanCollateral(collateral: LoanCollateral, decimals: number): LoanCollateral {
    return {
        ...collateral,
        assetType: collateral.assetType as AssetTypeIdentifier,
        amount: lamportsToUiAmount(collateral.amount, decimals)
    };
}

function convertLoanPnlStats(
    pnlResponseData: LoanPnlStats | undefined,
    principalDecimals: number | undefined,
    collateralDecimals: number | undefined
): LoanPnlStats | null {
    if (principalDecimals === undefined || collateralDecimals === undefined) return null;

    // Just convert top-level PnL values from the backend - BE handles all the logic
    return {
        ...pnlResponseData,
        collateralPnl: lamportsToUiAmount(pnlResponseData?.collateralPnl, collateralDecimals),
        netCollateralInflow: lamportsToUiAmount(pnlResponseData?.netCollateralInflow, collateralDecimals),
        netPrincipalInflow: lamportsToUiAmount(pnlResponseData?.netPrincipalInflow, principalDecimals),
        initialUserCollateralContribution: lamportsToUiAmount(
            pnlResponseData?.initialUserCollateralContribution,
            collateralDecimals
        )
    };
}

function convertPositionValueChartData(dataPoints: PositionValueDataPoint[]): PositionValueChartData[] {
    return dataPoints.map((point) => ({
        date: point.date,
        collateralTokenAmount: point.collateralTokenAmount,
        principalValueUsd: point.principalValueUsd,
        collateralValueUsd: point.collateralValueUsd,
        netPositionValueUsd: point.netPositionValueUsd,
        netPositionValueTokenAmount: point.netPositionValueTokenAmount,
        collateralTokenAmountOfDebt: point.collateralTokenAmountOfDebt,
        isProjection: point.isProjection,
        collateralMint: point.collateralMint
    }));
}

/**
 * Converts PnL chart data from backend format to frontend format.
 * NOTE: This function does NOT do chart-type-specific USD<->token conversions.
 * Those conversions happen in transformChartData (PositionValueChartBE.tsx) which
 * knows the selected chart type and denomination.
 */

function convertPnlChartData(dataPoints: PnLDataPoint[], collateralDecimals?: number): PnlChartData[] {
    return dataPoints.map((point) => {
        return {
            date: point.date,
            principalValueUsd: point.principalValueUsd,
            collateralValueUsd: point.collateralValueUsd,
            netPositionValueUsd: point.netPositionValueUsd,
            netPositionValueTokenAmount: point.netPositionValueTokenAmount,
            usdPnl: point.usdPnl,
            tokenPnl: lamportsToUiAmount(point.tokenPnl, collateralDecimals),
            collateralPrice: point.collateralPrice,
            netCollateralTransferTokenAmount: point.netCollateralTransferTokenAmount,
            netCollateralTransferUsdAmount: point.netCollateralTransferUsdAmount,
            isProjection: point.isProjection
        };
    });
}

function convertRateHistoryChartData(dataPoints: RateHistoryDataPoint[]): RateHistoryChartData[] {
    return dataPoints.map((point) => ({
        date: point.date,
        // BE returns APY as percentage (0-100), FE expects decimal (0-1)
        netApy: percentUiToDecimals(point.netApy),
        collateralApy: percentUiToDecimals(point.collateralApy),
        borrowApy: percentUiToDecimals(point.borrowApy),
        collateralMint: point.collateralMint
    }));
}

function convertLoanChartsEvents(events: ChartDataMarker[]): LoanChartsEvent[] {
    return events.map((e) => ({
        ...e
    }));
}

function convertStrategyInfo(strategy: StrategyInfo, getDecimals: GetDecimals): StrategyInfo {
    const { strategy: strategyInfo, terms, externalYieldInfo: eyi, ...rest } = strategy;
    const decimals = getDecimals(strategyInfo.principalMint);

    const convertedStrategy = convertStrategy(strategyInfo, decimals);
    const convertedExternalYieldInfo = eyi ? convertExternalYieldInfo(eyi, decimals) : undefined;
    const convertedTerms = convertStrategyTerms(terms, getDecimals, decimals);
    const convertedBorrowCapMonitor = convertCapMonitor(strategy.borrowCapMonitor, decimals);
    const convertedSupplyCapMonitor = convertCapMonitor(strategy.supplyCapMonitor, decimals);
    const convertedWithdrawCapMonitor = convertCapMonitor(strategy.withdrawCapMonitor, decimals);

    // Spread `rest` (without the optional `externalYieldInfo`) so strategies with no external
    // yield don't carry an `externalYieldInfo: undefined` key into the output — `filterNullableRecord`
    // downstream drops any object with an undefined top-level value.
    return {
        ...rest,
        strategy: convertedStrategy,
        ...(convertedExternalYieldInfo !== undefined && { externalYieldInfo: convertedExternalYieldInfo }),
        terms: convertedTerms,
        borrowCapMonitor: convertedBorrowCapMonitor,
        supplyCapMonitor: convertedSupplyCapMonitor,
        withdrawCapMonitor: convertedWithdrawCapMonitor
    };
}

function convertCapMonitor(capMonitor: ParsedCapMonitor, decimals: number): ParsedCapMonitor {
    return {
        ...capMonitor,
        principal1hr: bigIntAmountTag(capMonitor.principal1hr, decimals),
        principal24hr: bigIntAmountTag(capMonitor.principal24hr, decimals)
    };
}

function convertStrategy(strategy: Strategy, decimals: number): Strategy {
    return {
        ...strategy,
        // Convert token amounts using lamportsToUiAmount
        tokenBalance: lamportsToUiAmount(strategy.tokenBalance, decimals),
        originationCap: lamportsToUiAmount(strategy.originationCap, decimals),
        externalYieldAmount: lamportsToUiAmount(strategy.externalYieldAmount, decimals),
        currentDeployedAmount: lamportsToUiAmount(strategy.currentDeployedAmount, decimals),
        outstandingInterestAmount: lamportsToUiAmount(strategy.outstandingInterestAmount, decimals),
        feeClaimable: lamportsToUiAmount(strategy.feeClaimable, decimals),
        cumulativePrincipalOriginated: lamportsToUiAmount(strategy.cumulativePrincipalOriginated, decimals),
        cumulativeInterestAccrued: lamportsToUiAmount(strategy.cumulativeInterestAccrued, decimals),
        interestPerSecond: strategy.interestPerSecond / Math.pow(10, decimals),

        // Convert basis points using bpsToUiDecimals
        liquidityBuffer: bpsToUiDecimals(strategy.liquidityBuffer),
        interestFee: bpsToUiDecimals(strategy.interestFee),
        principalFee: bpsToUiDecimals(strategy.principalFee),
        originationFee: bpsToUiDecimals(strategy.originationFee),

        // Keep non-numeric values and counts as is
        id: strategy.id,
        address: strategy.address,
        version: strategy.version,
        nonce: strategy.nonce,
        bump: strategy.bump,
        principalMint: strategy.principalMint,
        lender: strategy.lender,
        externalYieldSource: strategy.externalYieldSource,
        lastAccruedTimestamp: strategy.lastAccruedTimestamp,
        cumulativeLoanCount: strategy.cumulativeLoanCount,
        activeLoanCount: strategy.activeLoanCount,
        marketInformation: strategy.marketInformation,
        closed: strategy.closed,
        lastInteractedTime: strategy.lastInteractedTime,
        lastInteractedTxn: strategy.lastInteractedTxn,
        createdAt: strategy.createdAt
    };
}

export function calculateWAvgApy(strategy: StrategyInfo, fixedApy: number): number {
    const externalYieldInfo = strategy.externalYieldInfo;
    const strategyCurrentDeployed = strategy.strategy.currentDeployedAmount;

    const totalDeployed = strategyCurrentDeployed;
    const externalBalance = externalYieldInfo?.balance ?? 0;

    const totalBalance = totalDeployed + externalBalance + strategy.strategy.tokenBalance;

    if (totalBalance === 0) return 0;

    const fixedWeight = totalDeployed / totalBalance;
    const externalWeight = externalBalance / totalBalance;
    const fixedApyWeighted = fixedApy * fixedWeight; // fixed apy is already minus fee

    const externalApyWeighted = externalYieldInfo ? externalYieldInfo.apy * externalWeight : 0;

    const externalApyMinusFee = externalApyWeighted * (1 - strategy.strategy.interestFee);
    const totalApyMinusFee = fixedApyWeighted + externalApyMinusFee;
    return totalApyMinusFee;
}

function convertBorrowCap({ principalMint, perLoan, global }: BorrowCap, getDecimals: GetDecimals): BorrowCap {
    const decimals = getDecimals(principalMint);
    return {
        principalMint,
        perLoan: lamportsToUiAmount(perLoan, decimals),
        global: lamportsToUiAmount(global, decimals)
    };
}

function convertPrincipalMarketStats(
    mint: string,
    stats: PrincipalMarketStats,
    getDecimals: GetDecimals
): PrincipalMarketStats {
    const decimals = getDecimals(mint);
    return {
        principalOriginated: lamportsToUiAmount(stats.principalOriginated, decimals),
        principalUtilized: lamportsToUiAmount(stats.principalUtilized, decimals),
        principalPrice: stats.principalPrice,
        wAvgBorrowApy: stats.wAvgBorrowApy,
        wAvgLendApy: stats.wAvgLendApy,
        durationToWAvgBorrowApy: (stats.durationToWAvgBorrowApy ?? []).map((b) => ({
            ...b,
            principalDue: lamportsToUiAmount(b.principalDue, decimals)
        }))
    };
}

function convertStrategyStats(
    mint: string,
    stats: PrincipalMarketStrategyStats,
    getDecimals: GetDecimals
): PrincipalMarketStrategyStats {
    const decimals = getDecimals(mint);
    return {
        totalDeposits: lamportsToUiAmount(stats.totalDeposits, decimals),
        durationToMinApy: stats.durationToMinApy.map((s) => ({ ...s, apy: bpsToUiDecimals(s.apy) }))
    };
}

function convertMergeOptionGroup(group: MergeOptionGroup): MergeOptionGroup {
    return {
        ...group,
        targetStrategyApy: bpsToUiDecimals(group.targetStrategyApy)
    };
}

function convertRankedMergeOption(option: RankedMergeOption): RankedMergeOption {
    return {
        ...option,
        weightedApyBefore: bpsToUiDecimals(option.weightedApyBefore),
        weightedApyAfter: bpsToUiDecimals(option.weightedApyAfter),
        weightedApyImprovement: bpsToUiDecimals(option.weightedApyImprovement),
        groups: option.groups.map(convertMergeOptionGroup)
    };
}

/** Merge planner quote: APY fields on the wire are cbps → UI decimals (same as {@link convertStrategyStats}). */
export function convertMergeLoanPlan(quote: MergePlannerRankedResponse): MergePlannerRankedResponse {
    return {
        weightedApyBefore: bpsToUiDecimals(quote.weightedApyBefore),
        lowestApyOption: convertRankedMergeOption(quote.lowestApyOption),
        fewestRemainingLoansOption: convertRankedMergeOption(quote.fewestRemainingLoansOption)
    };
}

function convertMarketInformation(
    marketInformation: ParsedMarketInformation,
    getDecimals: GetDecimals
): ParsedMarketInformation {
    const decimals = getDecimals(marketInformation.principalMint);

    return {
        ...marketInformation,
        max1hrBorrow: bigIntAmountTag(marketInformation.max1hrBorrow, decimals),
        max24hrBorrow: bigIntAmountTag(marketInformation.max24hrBorrow, decimals),
        maxOutstandingBorrow: bigIntAmountTag(marketInformation.maxOutstandingBorrow, decimals),
        max1hrSupply: bigIntAmountTag(marketInformation.max1hrSupply, decimals),
        max24hrSupply: bigIntAmountTag(marketInformation.max24hrSupply, decimals),
        maxOutstandingSupply: bigIntAmountTag(marketInformation.maxOutstandingSupply, decimals),
        max1hrWithdraw: bigIntAmountTag(marketInformation.max1hrWithdraw, decimals),
        max24hrWithdraw: bigIntAmountTag(marketInformation.max24hrWithdraw, decimals),
        maxOutstandingWithdraw: bigIntAmountTag(marketInformation.maxOutstandingWithdraw, decimals)
    };
}

function convertMarketInformationAssetInfo(
    marketInformationAssetInfo: MarketInformationAssetData,
    getDecimals: GetDecimals
): MarketInformationAssetData {
    const decimals = getDecimals(marketInformationAssetInfo.quoteMint);

    return {
        ...marketInformationAssetInfo,
        maxUncertainty: bpsToUiDecimals(marketInformationAssetInfo.maxUncertainty),
        maxCollateralAllocationPct: bigIntPercentTag(marketInformationAssetInfo.maxCollateralAllocationPct),
        currentAllocationAmount: bigIntAmountTag(marketInformationAssetInfo.currentAllocationAmount, decimals),
        ltv: bpsToUiDecimals(marketInformationAssetInfo.ltv),
        liquidationThreshold: bpsToUiDecimals(marketInformationAssetInfo.liquidationThreshold)
    };
}

function convertMarketInformationWithLtvInfo(
    marketInformation: ParsedMarketInformationWithLtvInfo,
    getDecimals: GetDecimals
): ParsedMarketInformationWithLtvInfo {
    const convertedMarketInformation = convertMarketInformation(marketInformation.marketInformation, getDecimals);
    const convertedMarketInformationAssetInfo = marketInformation.marketInformationAssetInfo.map((asset) =>
        convertMarketInformationAssetInfo(asset, getDecimals)
    );

    return {
        marketInformation: convertedMarketInformation,
        marketInformationAssetInfo: convertedMarketInformationAssetInfo,
        underlyingAssets: marketInformation.underlyingAssets
    };
}

function convertOfferQuote<T extends MaxQuote | BestQuote>(
    quote: T,
    principalMint: string,
    getDecimals: GetDecimals
): T {
    const decimals = getDecimals(principalMint);
    return {
        ...quote,
        apy: bpsToUiDecimals(quote.apy),
        ltv: bpsToUiDecimals(quote.ltv),
        liquidationThreshold: bpsToUiDecimals(quote.liquidationThreshold),
        principalAvailable: lamportsToUiAmount(quote.principalAvailable, decimals)
    };
}

function convertOrderbookQuote(quote: OrderbookQuote, principalMint: string, getDecimals: GetDecimals): OrderbookQuote {
    const decimals = getDecimals(principalMint);
    return {
        ...quote,
        apy: bpsToUiDecimals(quote.apy),
        maxPrincipalAvailable: lamportsToUiAmount(quote.maxPrincipalAvailable, decimals),
        sumPrincipalAvailable: lamportsToUiAmount(quote.sumPrincipalAvailable, decimals)
    };
}

function convertExternalYieldInfo(
    externalYieldInfo: ExternalYieldInfo | undefined,
    decimals: number | undefined
): ExternalYieldInfo | undefined {
    if (!externalYieldInfo) return undefined;

    return {
        ...externalYieldInfo,
        apy: bpsToUiDecimals(externalYieldInfo.apy),
        balance: lamportsToUiAmount(externalYieldInfo.balance, decimals)
    };
}

function convertLendingVaultDepositSummary(
    lendingVaultDepositSummary: UserPositionDepositSummaryResponse,
    vaultToPrincipalMint: Map<string, LendingVaultExpanded>,
    getDecimals: GetDecimals
): UserPositionDepositSummaryResponse {
    const formatted: UserPositionDepositSummaryResponse = {};

    for (const [vaultAddress, summary] of Object.entries(lendingVaultDepositSummary)) {
        const decimals = getDecimals(vaultToPrincipalMint.get(vaultAddress)?.vault.principalMint ?? "");

        formatted[vaultAddress] = {
            ...summary,
            sumOfDeposits: lamportsToUiAmount(summary.sumOfDeposits, decimals),
            sumOfWithdrawals: lamportsToUiAmount(summary.sumOfWithdrawals, decimals),
            vaultActions: summary.vaultActions.map((action) => ({
                ...action,
                amount: lamportsToUiAmount(action.amount, decimals)
            }))
        };
    }

    return formatted;
}

function convertLoopVault(
    loopVault: LoopVaultInfo,
    getDecimals: GetDecimals,
    getMetadata: GetMetadata
): LoopVaultInfoExpanded {
    const collateralToken = getMetadata(loopVault.collateralMint);
    const tokenMaturityDate = getPtTokenTermEnd(collateralToken);

    return {
        ...loopVault,
        tags: getLoopFilterTagsFromLoopTags(loopVault.tags),
        collateralApyPct: percentUiToDecimals(loopVault.collateralApyPct),
        maxLeveragedApyPct: percentUiToDecimals(loopVault.maxLeveragedApyPct),
        collateralDeposited: lamportsToUiAmount(loopVault.collateralDeposited, getDecimals(loopVault.collateralMint)),
        principalAmountAvailable: lamportsToUiAmount(
            loopVault.principalAmountAvailable,
            getDecimals(loopVault.principalMint)
        ),
        wAvgApy: percentUiToDecimals(loopVault.wAvgApy),
        oneWeekCollateralDepositsUsd: loopVault.oneWeekCollateralDepositsUsd,
        totalPrincipalDeployed: lamportsToUiAmount(
            loopVault.totalPrincipalDeployed,
            getDecimals(loopVault.principalMint)
        ),
        //Expanded types
        maturityTimestamp: tokenMaturityDate ? tokenMaturityDate.getTime() / 1000 : undefined,
        protocols: getProtocolsFromLoopTags(loopVault.tags),
        assetTagType: getLoopAssetTypeTagFromLoopTags(loopVault.tags),
        metadataTags: getLoopMetadataTagsFromLoopTags(loopVault.tags),
        quoteFetchParams: loopVault.quoteFetchParams
    };
}

export function calculateNetApyLive({
    loanCollateral,
    collateralApyPct,
    collateralUsdValue,
    principalApyPct,
    principalYieldPct,
    totalDebtUsdValue,
    collateralYields,
    oraclePrices
}: {
    loanCollateral: LoanCollateralExpanded | undefined;
    collateralApyPct: number | undefined;
    collateralUsdValue: number | undefined;
    principalApyPct: number | undefined;
    principalYieldPct: number | undefined;
    totalDebtUsdValue: number | undefined;
    collateralYields?: Record<string, number>;
    oraclePrices?: Map<string, OracleQuote | undefined>;
}): number | undefined {
    if (
        collateralApyPct === undefined ||
        collateralUsdValue === undefined ||
        principalApyPct === undefined ||
        principalYieldPct === undefined ||
        totalDebtUsdValue === undefined ||
        loanCollateral === undefined
    )
        return undefined;

    let collateralApy = collateralApyPct;

    // CLMM LP positions earn fees based on the position's liquidity band, not the
    // underlying-token yield in `collateralYieldPct`. Override with client-computed
    // position yield from the Whirlpool SDK.
    if (loanCollateral.clmmPosition && isWhirlpoolPosition(loanCollateral.clmmPosition)) {
        const { position, totalPrice, whirlpool } = loanCollateral.clmmPosition;

        const whirlpoolOffchain = loanCollateral.metadata.whirlpoolOffchain;
        if (whirlpoolOffchain) {
            const positionYield = getPositionYield({
                positionLiquidity: new BN(position.totalLiquidity),
                poolLiquidity: new BN(whirlpool.liquidity),
                whirlpoolOffchain: whirlpoolOffchain,
                positionUsdValue: totalPrice,
                tokenAAmount: loanCollateral.clmmPosition.tokenA.amount,
                tokenBAmount: loanCollateral.clmmPosition.tokenB.amount,
                tokenAMint: loanCollateral.clmmPosition.tokenA.mint,
                tokenBMint: loanCollateral.clmmPosition.tokenB.mint,
                collateralYields,
                oraclePrices
            });

            collateralApy = positionYield.positionYieldOverTvlAnnual;
        }
    }

    const collateralYieldUsd = collateralUsdValue * collateralApy;
    const principalCostUsd = totalDebtUsdValue * (principalApyPct + principalYieldPct);
    const netCapital = collateralUsdValue - totalDebtUsdValue;
    if (netCapital <= 0) return 0;

    const netProfitUsd = collateralYieldUsd - principalCostUsd;
    return netProfitUsd / netCapital;
}

function convertParsedLendingVault(vault: ParsedLendingVault, decimals: number): ParsedLendingVault {
    return {
        ...vault,
        cumulativePrincipalDeposited: lamportsToUiAmount(vault.cumulativePrincipalDeposited, decimals),
        lpSupply: lamportsToUiAmount(vault.lpSupply, decimals),
        maxEarlyUnstakeFee: bpsToUiDecimals(vault.maxEarlyUnstakeFee)
    };
}

function convertSingleAssetTerms(terms: AssetTerms, decimals: number): AssetTerms {
    return {
        ...terms,
        allocationInfo: {
            maxAllocationPct: bigIntPercentTag(terms.allocationInfo.maxAllocationPct),
            currentAllocationAmount: bigIntAmountTag(terms.allocationInfo.currentAllocationAmount, decimals)
        },
        durationAndApys: terms?.durationAndApys?.map(([duration, apy]) => [duration, bpsToUiDecimals(apy)]) || []
    };
}

function convertStrategyTerms(data: StrategyTerms, getDecimals: GetDecimals, principalDecimals: number): StrategyTerms {
    const assetTerms: Record<string, AssetTerms> = {};

    for (const [mint, terms] of Object.entries(data.assetTerms)) {
        const collateralDecimals = getDecimals(mint);
        assetTerms[mint] = convertSingleAssetTerms(terms, collateralDecimals);
    }

    return {
        ...data,
        assetTerms: assetTerms,
        supplyCaps: convertPrincipalCaps(data.supplyCaps, principalDecimals),
        borrowCaps: convertPrincipalCaps(data.borrowCaps, principalDecimals),
        withdrawCaps: convertPrincipalCaps(data.withdrawCaps, principalDecimals)
    };
}

function convertPrincipalCaps(caps: ParsedPrincipalCaps, decimals: number): ParsedPrincipalCaps {
    return {
        max1hr: bigIntAmountTag(caps.max1hr, decimals),
        max24hr: bigIntAmountTag(caps.max24hr, decimals),
        maxOutstanding: bigIntAmountTag(caps.maxOutstanding, decimals)
    };
}

function convertParsedCollateralTimelockArgs(
    parsedCollateralTerms: ParsedAddCollateralTimelockArgs
): ParsedAddCollateralTimelockArgs {
    return {
        ...parsedCollateralTerms,
        collateralTerms: convertParsedCollateralTerms(parsedCollateralTerms.collateralTerms),
        updateAssetDataParams: convertParsedUpdateAssetDataParams(parsedCollateralTerms.updateAssetDataParams)
    };
}

function convertParsedCollateralTerms(parsedCollateralTerms: ParsedCollateralTerms): ParsedCollateralTerms {
    const terms: bigint[] = [];
    for (const term of parsedCollateralTerms.terms) {
        terms.push(bigIntPercentTag(term));
    }
    return {
        ...parsedCollateralTerms,
        terms: terms
    };
}

function convertParsedUpdateAssetDataParams(
    parsedUpdateAssetDataParams: ParsedUpdateAssetDataParams
): ParsedUpdateAssetDataParams {
    return {
        ...parsedUpdateAssetDataParams,
        maxUncertainty: bpsToUiDecimals(parsedUpdateAssetDataParams.maxUncertainty),
        ltv: bpsToUiDecimals(parsedUpdateAssetDataParams.ltv),
        liquidationThreshold: bpsToUiDecimals(parsedUpdateAssetDataParams.liquidationThreshold),
        maxCollateralAllocationPct: bigIntPercentTag(parsedUpdateAssetDataParams.maxCollateralAllocationPct)
    };
}

function convertUpdateCapsParams(caps: UpdateCapsParams, decimals: number): UpdateCapsParams {
    return {
        ...caps,
        borrowCaps: caps.borrowCaps ? convertPrincipalCaps(caps.borrowCaps, decimals) : undefined,
        supplyCaps: caps.supplyCaps ? convertPrincipalCaps(caps.supplyCaps, decimals) : undefined,
        withdrawCaps: caps.withdrawCaps ? convertPrincipalCaps(caps.withdrawCaps, decimals) : undefined
    };
}

function convertParsedUpdateStrategySettingsParams(
    updateStrategySettingsParams: ParsedUpdateStrategySettingsParams,
    decimals: number
): ParsedUpdateStrategySettingsParams {
    return {
        ...updateStrategySettingsParams,
        originationCap: lamportsToUiAmount(updateStrategySettingsParams.originationCap, decimals),
        originationFee: bpsToUiDecimals(updateStrategySettingsParams.originationFee),
        interestFee: bpsToUiDecimals(updateStrategySettingsParams.interestFee),
        principalFee: bpsToUiDecimals(updateStrategySettingsParams.principalFee),
        liquidityBuffer: bpsToUiDecimals(updateStrategySettingsParams.liquidityBuffer)
    };
}

function convertUpdateVaultSettingsParams(
    updateVaultSettingsParams: ParsedUpdateVaultSettingsParams
): ParsedUpdateVaultSettingsParams {
    return {
        ...updateVaultSettingsParams,
        maxEarlyUnstakeFeeCbps: bpsToUiDecimals(updateVaultSettingsParams.maxEarlyUnstakeFeeCbps)
    };
}

function convertTimelock(timeLock: ParsedTimeLock, getDecimals: GetDecimals): ParsedTimeLock {
    const update = timeLock.update;
    const decimals = getDecimals(timeLock.principalMint);

    switch (update.type) {
        case TimeLockUpdateType.AddCollateral: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.AddCollateral,
                    params: convertParsedCollateralTimelockArgs(update.params)
                }
            };
        }
        case TimeLockUpdateType.UpdateCollateralAllocationPct: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.UpdateCollateralAllocationPct,
                    params: convertParsedUpdateAssetDataParams(update.params)
                }
            };
        }
        case TimeLockUpdateType.UpdateLtv: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.UpdateLtv,
                    params: convertParsedUpdateAssetDataParams(update.params)
                }
            };
        }
        case TimeLockUpdateType.RemoveCollateral: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.RemoveCollateral,
                    params: convertParsedCollateralTerms(update.params)
                }
            };
        }
        case TimeLockUpdateType.UpdateApy: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.UpdateApy,
                    params: convertParsedCollateralTerms(update.params)
                }
            };
        }
        case TimeLockUpdateType.CapsUpdate: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.CapsUpdate,
                    params: convertUpdateCapsParams(update.params, decimals)
                }
            };
        }
        case TimeLockUpdateType.UpdateStrategySettings: {
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.UpdateStrategySettings,
                    params: convertParsedUpdateStrategySettingsParams(update.params, decimals)
                }
            };
        }
        case TimeLockUpdateType.UpdateVaultSettings:
            return {
                ...timeLock,
                update: {
                    type: TimeLockUpdateType.UpdateVaultSettings,
                    params: convertUpdateVaultSettingsParams(update.params)
                }
            };

        // types that don't have any conversion, just pass-through
        default:
            return {
                ...timeLock,
                update: {
                    type: update.type,
                    params: update.params
                }
            };
    }
}

export function convertLendingVaultMetadata(metadata: LendingVaultMetadata, decimals: number): LendingVaultMetadata {
    return {
        ...metadata,
        depositCap: metadata?.depositCap ? lamportsToUiAmount(metadata.depositCap, decimals) : undefined
    };
}

// Fee/threshold fields rescale to 0.01-decimal form so consumers can pass them straight to
// `formatPercent` — mirrors how snapshot bps fields are handled above. u64 financials stay
// `bigint` end-to-end. `withdrawalPeriod` / `withdrawalCancelCooldownSeconds` / `defaultTimelockSeconds`
// / `defaultVotingPeriodSeconds` are seconds (not bps) and pass through untouched.
//
// Scale gotcha: `managementFeeBps` and `rejectionThresholdBps` are BPS_PRECISION = 1e6 ("cbps"),
// so they rescale with "cbps". `fastWithdrawalCutBp` / `normalWithdrawalCutBp` /
// `instantWithdrawalWindowLimitBp` / `reservesShareBp` are regular bps (1e4) and rescale with "bps".
export function convertEarnExponentAccount(account: EarnExponentAccount | undefined): EarnExponentAccount | undefined {
    if (!account) return undefined;
    const rc = account.reservesConfig;
    const pvc = account.proposalVoteConfig;
    return {
        ...account,
        normalWithdrawalCutBp:
            account.normalWithdrawalCutBp == null ? undefined : bpsToUiDecimals(account.normalWithdrawalCutBp, "bps"),
        reservesConfig: !rc
            ? rc
            : {
                  ...rc,
                  reservesShareBp: rc.reservesShareBp == null ? undefined : bpsToUiDecimals(rc.reservesShareBp, "bps"),
                  managementFeeBps:
                      rc.managementFeeBps == null ? undefined : bpsToUiDecimals(rc.managementFeeBps, "cbps"),
                  fastWithdrawalCutBp:
                      rc.fastWithdrawalCutBp == null ? undefined : bpsToUiDecimals(rc.fastWithdrawalCutBp, "bps"),
                  instantWithdrawalWindowLimitBp:
                      rc.instantWithdrawalWindowLimitBp == null
                          ? undefined
                          : bpsToUiDecimals(rc.instantWithdrawalWindowLimitBp, "bps")
              },
        proposalVoteConfig: !pvc
            ? pvc
            : {
                  ...pvc,
                  rejectionThresholdBps:
                      pvc.rejectionThresholdBps == null ? undefined : bpsToUiDecimals(pvc.rejectionThresholdBps, "cbps")
              }
    };
}

// Queue slice is still on raw `bigint` (not yet migrated to TokenAmount); the TokenAmount-emitting
// vault list/detail + position converters live in `utils/view/earn.ts` and
// `utils/view/earn-positions.ts`.
function convertEarnWithdrawalRequest(req: EarnWithdrawalRequest): EarnWithdrawalRequest {
    return req;
}

function convertEarnQueueResponse(resp: EarnQueueResponse): EarnQueueResponse {
    return {
        ...resp,
        openRequests: resp.openRequests.map(convertEarnWithdrawalRequest)
    };
}

function convertEarnActivityResponse(resp: EarnActivityResponse): EarnActivityResponse {
    return resp;
}

function convertEarnPortfolioEvent(event: EarnPortfolioEvent): EarnPortfolioEvent {
    return event;
}

function convertEarnPortfolioHistoryPoint(point: EarnPortfolioHistoryPoint): EarnPortfolioHistoryPoint {
    return point;
}

// Pass-through: e18 fixed-point values stay `string` until the chart/visualization boundary, where
// the feature view hook converts them via navPerShareE18ToNumber. No UI-unit rescale applies here.
function convertEarnPositionChart(resp: EarnPositionChartResponse): EarnPositionChartResponse {
    return resp;
}

function convertExternalRewardsResponse(
    response: UserVaultExternalRewardsPnlResponse,
    getMetadata: GetMetadata
): UserVaultExternalRewardsPnlResponse {
    const result: UserVaultExternalRewardsPnlResponse = {};

    // Iterate through all vault addresses
    Object.entries(response).forEach(([vaultAddress, vaultRewards]) => {
        if (!vaultRewards) return;

        result[vaultAddress] = {};

        // Iterate through all stake accounts for this vault
        Object.entries(vaultRewards).forEach(([stakeAccount, rewardsForStake]) => {
            result[vaultAddress][stakeAccount] = {};

            // Iterate through all reward mints for this stake account
            Object.entries(rewardsForStake).forEach(([rewardMint, rewardInfo]) => {
                const metadata = getMetadata(rewardMint);
                if (!metadata) return;

                result[vaultAddress][stakeAccount][rewardMint] = {
                    ...rewardInfo,
                    rewardMetadata: metadata,
                    claimedRewards: lamportsToUiAmount(rewardInfo.claimedRewards, metadata.decimals),
                    pendingRewards: lamportsToUiAmount(rewardInfo.pendingRewards, metadata.decimals)
                };
            });
        });
    });

    return result;
}

function convertLendingVaultInfo(
    lendingVault: ParsedLendingVaultInfo,
    getDecimals: GetDecimals
): ParsedLendingVaultInfo {
    const decimals = getDecimals(lendingVault.vault.principalMint);

    const parsedVault = convertParsedLendingVault(lendingVault.vault, decimals);
    const vaultMetadata = convertLendingVaultMetadata(lendingVault.vaultMetadata, decimals);
    const rewards = convertParsedVaultRewardsSchedule(lendingVault.vaultRewardsSchedules, getDecimals);

    return {
        vault: parsedVault,
        vaultMetadata: vaultMetadata,
        vaultStrategy: lendingVault.vaultStrategy,
        vaultRewardsSchedules: rewards,
        vaultRewardsEmittedAmounts: lendingVault.vaultRewardsEmittedAmounts,
        totalOutstandingLoanCollateralValueUsd: lendingVault.totalOutstandingLoanCollateralValueUsd
    };
}

function convertParsedVaultRewardsSchedule(
    rewards: ParsedVaultRewardsSchedule[],
    getDecimals: GetDecimals
): ParsedVaultRewardsSchedule[] {
    return rewards.map((reward) => ({
        ...reward,
        totalWeightedStakeSupply: stringLamportsToUiAmountDecimal(
            reward.totalWeightedStakeSupply,
            getDecimals(reward.rewardMint)
        ),
        totalEmissionsAmount: lamportsToUiAmount(reward.totalEmissionsAmount, getDecimals(reward.rewardMint)),
        emissionsClaimed: lamportsToUiAmount(reward.emissionsClaimed, getDecimals(reward.rewardMint)),
        emissionsPerSecond: stringLamportsToUiAmountDecimal(reward.emissionsPerSecond, getDecimals(reward.rewardMint))
    }));
}

function convertLendingVaultAllocation(
    allocation: LendingVaultAllocation,
    principalDecimals: number
): LendingVaultAllocation {
    return {
        ...allocation,
        wAvgApy: bpsToUiDecimals(allocation.wAvgApy),
        totalPrincipalDeployed: lamportsToUiAmount(allocation.totalPrincipalDeployed, principalDecimals)
    };
}

function convertPortfolioRewardsBreakdown(
    portfolioRewards: PortfolioRewards | undefined,
    rewardPointsPerSecond: RewardPointsPerSecond,
    getMetadata: GetMetadata
): PortfolioRewardsBreakdown[] {
    const idle = rewardPointsPerSecond[LoopscalePointsAction.IdleCap]?.pointsPerSecond;

    const strategies = convertStrategyBreakdown(portfolioRewards?.strategies ?? {}, getMetadata, idle);
    const borrows = convertLoanBreakdown(portfolioRewards?.loans ?? {}, getMetadata, BreakdownActivity.Borrow, idle);
    const vaults = convertVaultBreakdown(portfolioRewards?.vaultStakes ?? {}, getMetadata, idle);
    const earnVaults = convertEarnVaultBreakdown(portfolioRewards?.earnVaults ?? {}, getMetadata, idle);
    const loops = convertLoanBreakdown(portfolioRewards?.loops ?? {}, getMetadata, BreakdownActivity.Loop, idle);

    const portfolioRewardBreakdown = combineCollections([strategies, borrows, vaults, earnVaults, loops]).filter(
        filterNullableRecord
    );

    return portfolioRewardBreakdown;
}

function convertStrategyBreakdown(
    strategies: Record<string, StrategyRewards>,
    getMetadata: GetMetadata,
    idle: number
): NullableRecord<PortfolioRewardsBreakdown>[] {
    if (Object.keys(strategies).length === 0) return [];
    const strategyBreakdown: NullableRecord<PortfolioRewardsBreakdown>[] = [];
    const now = getUnixTs();

    for (const [, strategy] of Object.entries(strategies)) {
        // Process ledger rewards
        if (strategy.ledgerRewards) {
            for (const ledgerReward of strategy.ledgerRewards) {
                const principalMetadata = getMetadata(ledgerReward.principalMint);
                const collateralMetadata = ledgerReward.loanCollateralAssetIdentifiers
                    ? ledgerReward.loanCollateralAssetIdentifiers.map(getMetadata).filter(filterTruthy)
                    : [];

                // Only add if we have valid metadata
                if (principalMetadata && ledgerReward.baseReward.isLenderReward) {
                    strategyBreakdown.push({
                        rewardId: ledgerReward.baseReward.rewardId,
                        usdValue: ledgerReward.baseReward.usdValue,
                        activity: BreakdownActivity.AdvancedLend,
                        pointsPerDay: getPointsPerDay(ledgerReward),
                        boost: roundToDecimals(ledgerReward.baseReward.multiplier / idle, 2),
                        totalPoints: getLedgerTotalPoints(ledgerReward, now),
                        principalMetadata,
                        collateralMetadata,
                        isActive: ledgerReward.baseReward.isActive
                    });
                }
            }
        }

        // Process external yield rewards separately
        if (strategy.externalYieldRewards) {
            const externalReward = strategy.externalYieldRewards;
            const principalMetadata = getMetadata(externalReward.principalMint);
            if (principalMetadata && externalReward.baseReward.isLenderReward) {
                strategyBreakdown.push({
                    rewardId: externalReward.baseReward.rewardId,
                    usdValue: externalReward.baseReward.usdValue,
                    activity: BreakdownActivity.IdleLend,
                    pointsPerDay: externalReward.baseReward.pointsPerSecond * TIME.DAY,
                    boost: roundToDecimals(externalReward.baseReward.multiplier / idle, 2),
                    totalPoints: externalReward.baseReward.lastUpdateValue,
                    principalMetadata,
                    externalYieldSource: externalReward.externalYieldSource,
                    isActive: externalReward.baseReward.isActive
                });
            }
        }
    }

    return strategyBreakdown;
}

function convertLoanBreakdown(
    loans: Record<string, LedgerReward[]>,
    getMetadata: GetMetadata,
    breakdown: BreakdownActivity,
    idle: number
): NullableRecord<PortfolioRewardsBreakdown>[] {
    if (Object.keys(loans).length === 0) return [];
    const loanBreakdown: NullableRecord<PortfolioRewardsBreakdown>[] = [];
    const now = getUnixTs();

    for (const [, loanRewards] of Object.entries(loans)) {
        if (!Array.isArray(loanRewards)) continue;

        for (const ledger of loanRewards) {
            const principalMetadata = getMetadata(ledger.principalMint);
            const collateralMetadata =
                ledger.loanCollateralAssetIdentifiers?.map(getMetadata).filter(filterTruthy) || [];

            if (principalMetadata && !ledger.baseReward.isLenderReward) {
                loanBreakdown.push({
                    rewardId: ledger.baseReward.rewardId,
                    usdValue: ledger.baseReward.usdValue,
                    activity: breakdown,
                    pointsPerDay: getPointsPerDay(ledger),
                    boost: roundToDecimals(ledger.baseReward.multiplier / idle, 2),
                    totalPoints: getLedgerTotalPoints(ledger, now),
                    principalMetadata,
                    collateralMetadata,
                    isActive: ledger.baseReward.isActive
                });
            }
        }
    }

    return loanBreakdown;
}

function convertVaultBreakdown(
    vaults: Record<string, VaultReward>,
    getMetadata: GetMetadata,
    idle: number
): NullableRecord<PortfolioRewardsBreakdown>[] {
    if (Object.keys(vaults).length === 0) return [];
    const vaultBreakdown: NullableRecord<PortfolioRewardsBreakdown>[] = [];
    const now = getUnixTs();

    for (const [, vault] of Object.entries(vaults)) {
        const principalMetadata = getMetadata(vault.vaultPrincipalMint);

        if (principalMetadata && vault.baseReward.isLenderReward) {
            vaultBreakdown.push({
                rewardId: vault.baseReward.rewardId,
                usdValue: vault.baseReward.usdValue,
                activity: BreakdownActivity.LendVault,
                pointsPerDay: getVaultPointsPerDay(vault),
                boost: roundToDecimals(vault.baseReward.multiplier / idle, 2),
                totalPoints: getVaultLedgerTotalPoints(vault, now),
                principalMetadata: principalMetadata,
                vaultMetadata: vault.vaultMetadata,
                isActive: vault.baseReward.isActive
            });
        }
    }

    return vaultBreakdown;
}

function convertEarnVaultBreakdown(
    earnVaults: Record<string, EarnVaultReward>,
    getMetadata: GetMetadata,
    idle: number
): NullableRecord<PortfolioRewardsBreakdown>[] {
    if (Object.keys(earnVaults).length === 0) return [];
    const earnVaultBreakdown: NullableRecord<PortfolioRewardsBreakdown>[] = [];
    const now = getUnixTs();

    for (const [, earnVault] of Object.entries(earnVaults)) {
        const principalMetadata = getMetadata(earnVault.acceptedAssetMint);

        if (principalMetadata && earnVault.baseReward.isLenderReward) {
            earnVaultBreakdown.push({
                rewardId: earnVault.baseReward.rewardId,
                usdValue: earnVault.baseReward.usdValue,
                activity: BreakdownActivity.EarnVault,
                pointsPerDay: getEarnVaultPointsPerDay(earnVault),
                boost: roundToDecimals(earnVault.baseReward.multiplier / idle, 2),
                totalPoints: getEarnVaultLedgerTotalPoints(earnVault, now),
                principalMetadata,
                vaultMetadata: earnVault.vaultMetadata,
                isActive: earnVault.baseReward.isActive
            });
        }
    }

    return earnVaultBreakdown;
}

function getPointsPerDay(ledger: LedgerReward) {
    if (isActiveLedgerReward(ledger)) {
        return ledger.baseReward.pointsPerSecond * TIME.DAY;
    }
    return 0;
}
function getVaultPointsPerDay(vault: VaultReward) {
    return vault.baseReward.pointsPerSecond * TIME.DAY;
}

function getEarnVaultPointsPerDay(earnVault: EarnVaultReward) {
    return earnVault.baseReward.pointsPerSecond * TIME.DAY;
}

function getLedgerTotalPoints(ledger: LedgerReward, now: number) {
    const timeToUse = isActiveLedgerReward(ledger) ? now : (ledger.baseReward.endTime ?? 0);
    const timeDelta = Math.max(0, timeToUse - ledger.baseReward.accruedTimeStamp);

    const totalPoints = ledger.baseReward.lastUpdateValue + ledger.baseReward.pointsPerSecond * timeDelta;

    return totalPoints;
}

function isActiveLedgerReward(ledger: LedgerReward) {
    return ledger.baseReward.isActive || ledger.baseReward.endTime === null;
}

function getVaultLedgerTotalPoints(vault: VaultReward, now: number) {
    const timeToUse = vault.baseReward.isActive ? now : (vault.baseReward.endTime ?? 0);
    const timeDelta = Math.max(0, timeToUse - vault.baseReward.accruedTimeStamp);
    const totalPoints = vault.baseReward.lastUpdateValue + vault.baseReward.pointsPerSecond * timeDelta;
    return totalPoints;
}
function healthSeverityScore(status: LoanHealthStatus): number {
    if (status === LoanHealthStatus.Danger) return 2;
    if (status === LoanHealthStatus.Warning) return 1;
    return 0;
}

function getStatusFromRisk(risk: number): LoanHealthStatus {
    if (risk >= 1.5) return LoanHealthStatus.Danger;
    if (risk >= 0.5) return LoanHealthStatus.Warning;
    return LoanHealthStatus.Healthy;
}

export function getGroupLoanHealth(loans: AbfLoanExpanded[] | undefined): {
    averageHealth: number | undefined;
    status: LoanHealthStatus | undefined;
    worstStatus: LoanHealthStatus | undefined;
} {
    if (!loans) return { averageHealth: undefined, status: undefined, worstStatus: undefined };

    let weightedSum = 0;
    let totalWeight = 0;
    let riskScore = 0;
    let worstStatus: LoanHealthStatus = LoanHealthStatus.Healthy;

    for (const loan of loans) {
        if (!isLoanActive(loan)) continue;
        const { health, totalOwedValue, loanHealthStatus } = calculateLoanHealth(loan);
        if (totalOwedValue <= 0 || !Number.isFinite(health)) continue;

        weightedSum += health * totalOwedValue;
        totalWeight += totalOwedValue;

        const severity = healthSeverityScore(loanHealthStatus);
        riskScore += severity * totalOwedValue;

        if (severity > healthSeverityScore(worstStatus)) {
            worstStatus = loanHealthStatus;
        }
    }

    if (totalWeight === 0) {
        return { averageHealth: 1, status: LoanHealthStatus.Healthy, worstStatus: LoanHealthStatus.Healthy };
    }

    const averageHealth = weightedSum / totalWeight;
    const normalizedRisk = riskScore / totalWeight;

    return {
        averageHealth,
        status: getStatusFromRisk(normalizedRisk),
        worstStatus
    };
}
function getEarnVaultLedgerTotalPoints(earnVault: EarnVaultReward, now: number) {
    const timeToUse = earnVault.baseReward.isActive ? now : (earnVault.baseReward.endTime ?? 0);
    const timeDelta = Math.max(0, timeToUse - earnVault.baseReward.accruedTimeStamp);
    const totalPoints = earnVault.baseReward.lastUpdateValue + earnVault.baseReward.pointsPerSecond * timeDelta;
    return totalPoints;
}

export function calculateTotalInterestAccrued(
    strategy: StrategyExpandedInfo | undefined,
    options?: {
        includeStrategyInterest?: boolean;
        includeExternalInterest?: boolean;
        includeCumulativeInterest?: boolean;
    }
): number {
    if (!strategy) return 0;

    const now = Math.round(getUnixTs());
    let totalInterest = options?.includeCumulativeInterest
        ? Math.max(strategy.strategy?.cumulativeInterestAccrued ?? 0, 0)
        : 0;

    // Calculate strategy interest if included
    if (options?.includeStrategyInterest) {
        const interestPerSecond = Math.max(strategy.strategy?.interestPerSecond.toNumber() ?? 0, 0);
        const timeDelta = Math.max(0, now - (strategy.strategy?.lastAccruedTimestamp ?? now));
        const strategyInterest = interestPerSecond * timeDelta;
        totalInterest += strategyInterest;
    }

    // Calculate external interest if included
    if (options?.includeExternalInterest) {
        const externalBalance = Math.max(strategy.externalYieldInfo?.balance ?? 0, 0);
        const externalYieldAmount = Math.max(strategy.strategy?.externalYieldAmount ?? 0, 0);
        const externalInterest = Math.max(externalBalance - externalYieldAmount, 0);
        totalInterest += externalInterest;
    }

    return totalInterest;
}

export function calculateFixedStrategyApy(strategy: StrategyInfo): number {
    const interestPerSecond = new Decimal(strategy.strategy.interestPerSecond);

    const yearlyInterest = interestPerSecond.mul(TIME.YEAR);

    const principal = strategy.strategy.currentDeployedAmount || 1;
    const annualRate = yearlyInterest.div(principal);

    const fixedApyMinusFee = annualRate.mul(1 - strategy.strategy.interestFee);

    return fixedApyMinusFee.toNumber();
}

export function getPtTokenTermEnd(token: TokenListMetadata | undefined): Date | undefined {
    if (!token || !token.name) {
        return undefined;
    }

    const metadataTermEnd = token.metadata?.termEnd;
    if (typeof metadataTermEnd === "number") {
        return new Date(metadataTermEnd * 1000);
    }

    const isRatex = token.name.includes("Ratex");
    const isExponent = token.name.includes("Exponent");

    if (!isRatex && !isExponent) {
        return undefined;
    }

    try {
        if (isRatex) {
            // Extract YYMM from PT-USD*-2506
            const datePart = token.name.split("-")[2];
            if (!datePart || datePart.length !== 4) return undefined;

            const year = 2000 + parseInt(datePart.substring(0, 2));
            const month = parseInt(datePart.substring(2, 4)) - 1; // JS months are 0-indexed

            // Set date to last day of the month
            const date = new Date(year, month + 1, 0);
            return date;
        } else if (isExponent) {
            // Extract DDMonYY from exponent-fragsol-10Jul25
            const parts = token.name.split("-");

            if (parts.length === 0) return undefined;

            const datePart = parts[parts.length - 1];
            // Extract numeric day part
            const dayMatch = datePart.match(/^(\d{1,2})/);
            if (!dayMatch) return undefined;
            const day = parseInt(dayMatch[1]);

            // Extract month part (3 letters)
            const monthMatch = datePart.match(/[A-Za-z]{3}/);
            if (!monthMatch) return undefined;
            const monthStr = monthMatch[0];

            // Extract year part (last 2 digits)
            const yearMatch = datePart.match(/\d{2}$/);
            if (!yearMatch) return undefined;
            const year = 2000 + parseInt(yearMatch[0]);

            // Convert month string to month number (0-indexed)
            const months = {
                jan: 0,
                feb: 1,
                mar: 2,
                apr: 3,
                may: 4,
                jun: 5,
                jul: 6,
                aug: 7,
                sep: 8,
                oct: 9,
                nov: 10,
                dec: 11
            };
            const month = months[monthStr.toLowerCase() as keyof typeof months];

            if (month === undefined) return undefined;

            return new Date(year, month, day);
        }
    } catch (e) {
        return undefined;
    }

    return undefined;
}

export function shortFormatTokenSymbol(token: TokenListMetadata | undefined) {
    if (!token?.name) return token?.symbol;
    if (!isPtToken(token)) return token.symbol;

    const parts = token.symbol.split("-");
    if (token.name.includes("Ratex")) {
        return parts[0];
    }

    // exponent tokens are formatted as PT-ONyc-13MAY26
    // safely return the original symbol if we incorrectly insert to allowed tokens
    if (parts.length >= 2) {
        return `${parts[0]}-${parts[1]}`;
    }
    return token.symbol;
}

export function isPtToken(token: TokenListMetadata | undefined): boolean {
    if (!token || !token.name) {
        return false;
    }

    const isRatex = token.name.includes("Ratex");
    const isExponent = token.name.includes("Exponent");

    return isRatex || isExponent;
}

// Filter loop by tag type
function getProtocolsFromLoopTags(tags: LoopTag[] | undefined): Protocol[] {
    return filterLoopTagsByType<Protocol>(tags, Protocol);
}

function getLoopFilterTagsFromLoopTags(tags: LoopTag[] | undefined): LoopFilterTags[] {
    return filterLoopTagsByType<LoopFilterTags>(tags, LoopFilterTags);
}

function getLoopAssetTypeTagFromLoopTags(tags: LoopTag[] | undefined): LoopAssetTypeTag[] {
    return filterLoopTagsByType<LoopAssetTypeTag>(tags, LoopAssetTypeTag);
}

function getLoopMetadataTagsFromLoopTags(tags: LoopTag[] | undefined): LoopMetadataTag[] {
    return filterLoopTagsByType<LoopMetadataTag>(tags, LoopMetadataTag);
}

function filterLoopTagsByType<T extends LoopTag>(
    tags: LoopTag[] | undefined,
    enumType: { [key: string]: string }
): T[] {
    if (!tags) return [];

    return tags
        .map((tag) => {
            if (Object.values(enumType).includes(tag)) {
                return tag as T;
            }
            return undefined;
        })
        .filter(filterTruthy);
}

export function convertPartnerInfo(rawPartner: PartnerInfo): PartnerInfo {
    return {
        partner: rawPartner.partner,
        defiLlamaIds: rawPartner.defiLlamaIds,
        hasActiveIntegration: rawPartner.hasActiveIntegration
    };
}

export function convertFarmingInfo(rawFarmingInfo: FarmingInfo): FarmingInfo {
    return {
        integrationId: rawFarmingInfo.integrationId,
        integrationName: rawFarmingInfo.integrationName,
        isFeatured: rawFarmingInfo.isFeatured,
        partners: rawFarmingInfo.partners,
        partnerRewards: rawFarmingInfo.partnerRewards,
        partnerPoints: rawFarmingInfo.partnerPoints
    };
}

export function convertPartnerPoint(rawPoint: PartnerPoint): PartnerPoint {
    return {
        id: rawPoint.id,
        partnerId: rawPoint.partnerId,
        actionType: rawPoint.actionType,
        integrationTargetMint: rawPoint.integrationTargetMint,
        integrationMultiplier: rawPoint.integrationMultiplier,
        isFeatured: rawPoint.isFeatured
    };
}

export function convertPartnerReward(rawReward: PartnerReward): PartnerReward {
    return {
        rewardScheduleId: rawReward.rewardScheduleId,
        partnerId: rawReward.partnerId,
        percentage: rawReward.percentage
    };
}

function convertTokenRestrictions(
    restrictions: TokenRestrictionResponse[] | undefined,
    getMetadata: GetMetadata
): RestrictionExpanded[] | undefined {
    if (!restrictions) return undefined;

    // Flatten restrictions and attach mint + metadata to each one
    return restrictions.flatMap((tokenRestriction) =>
        tokenRestriction.restrictions.map((restriction) => {
            const token = getMetadata(tokenRestriction.mint);

            return {
                ...restriction,
                token: token
            };
        })
    );
}
